import orderBy from 'lodash/orderBy';
import map from 'lodash/map';
import flatten from 'lodash/flatten.js';
import cloneDeep from 'lodash/cloneDeep.js';
import i18n from '../plugins/vue-i18n.js';
import User from '../entities/User.js';

export default {
    calendarText() {
        if (i18n.locale == 'en') {
            return {
                days: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
                months: [
                    'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October',
                    'November', 'December',
                ],
                monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                today: 'Today',
                now: 'Now',
                am: 'AM',
                pm: 'PM',
            };
        }

        return {
            days: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
            months: [
                'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre',
                'Novembre', 'Décembre',
            ],
            monthsShort: [
                'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.',
                'nov.', 'déc.',
            ],
            today: 'Aujourd\'hui',
            now: 'Maintenant',
            am: 'AM',
            pm: 'PM',
        };
    },

    partner({ authUser }) {
        if (!authUser.partner) {
            return 'default';
        }

        return authUser.partner.name.toLowerCase();
    },

    templatesAttributes(state) {
        if (empty(state.templatePlaceholders)) return [];

        const placeholders = cloneDeep(state.templatePlaceholders);

        if (!state.authUser.account.appointment_module) {
            delete placeholders.en.appointment;
            delete placeholders.fr.appointment;
        }

        if (!state.authUser.account.payment_module) {
            delete placeholders.en.payment;
            delete placeholders.fr.payment;
        }

        return orderBy(flatten(map(placeholders[i18n.locale], x => {
            return map(x, y => {
                return y;
            });
        })));
    },

    weekdays() {
        return [
            {
                index: 1,
                name: i18n.t('global.weekdays.monday'),
            },
            {
                index: 2,
                name: i18n.t('global.weekdays.tuesday'),
            },
            {
                index: 3,
                name: i18n.t('global.weekdays.wednesday'),
            },
            {
                index: 4,
                name: i18n.t('global.weekdays.thursday'),
            },
            {
                index: 5,
                name: i18n.t('global.weekdays.friday'),
            },
            {
                index: 6,
                name: i18n.t('global.weekdays.saturday'),
            },
            {
                index: 0,
                name: i18n.t('global.weekdays.sunday'),
            },
        ];
    },

    isAuthenticated({ authUser }) {
        return authUser && authUser.access_token;
    },

    hasAccessToAppointment({ authUser }) {
        const user = new User(authUser);

        return user.can('appointment module') && user.account.appointment_module;
    },

    hasAccessToCampaign({ authUser }) {
        const user = new User(authUser);

        return user.can('campaign module') && user.account.campaign_module;
    },

    hasAccessToPayment({ authUser }) {
        const user = new User(authUser);

        return user.can('payment module') && user.account.payment_module;
    },

    hasAccessToTeamSettings({ authUser }) {
        return new User(authUser).can('manage team settings');
    },

    hasAccessToViewOtherUsersConversations({ authUser }) {
        return new User(authUser).can('view other users conversations');
    },

    timezones() {
        const timezones = [
            'America/Edmonton',
            'America/Halifax',
            'America/Iqaluit',
            'America/Moncton',
            'America/Montreal',
            'America/Regina',
            'America/St_Johns',
            'America/Toronto',
            'America/Vancouver',
            'America/Whitehorse',
            'America/Winnipeg',
            'America/Yellowknife',

            'America/New_York',
            'America/Chicago',
            'America/Denver',
            'America/Phoenix',
            'America/Los_Angeles',
            'America/Anchorage',
            'America/Adak',
            'Pacific/Honolulu',
        ];

        return orderBy(
            timezones.map(timezone => {
                return {
                    value: timezone,
                    label: `(GMT${moment.tz(timezone).format('Z')}) ${timezone}`,
                };
            }),
            'label',
        );
    },

    countries() {
        return [
            { value: 'CA', label: i18n.t('global.countries.ca') },
            { value: 'US', label: i18n.t('global.countries.us') },
        ];
    },

    provinces() {
        return [
            { value: 'AB', label: i18n.t('global.provinces.ab') },
            { value: 'BC', label: i18n.t('global.provinces.bc') },
            { value: 'MB', label: i18n.t('global.provinces.mb') },
            { value: 'NB', label: i18n.t('global.provinces.nb') },
            { value: 'NL', label: i18n.t('global.provinces.nl') },
            { value: 'NS', label: i18n.t('global.provinces.ns') },
            { value: 'NT', label: i18n.t('global.provinces.nt') },
            { value: 'NU', label: i18n.t('global.provinces.nu') },
            { value: 'ON', label: i18n.t('global.provinces.on') },
            { value: 'PE', label: i18n.t('global.provinces.pe') },
            { value: 'QC', label: i18n.t('global.provinces.qc') },
            { value: 'SK', label: i18n.t('global.provinces.sk') },
            { value: 'YT', label: i18n.t('global.provinces.yt') },
        ];
    },

    states() {
        return [
            { value: 'AL', label: i18n.t('global.states.al') },
            { value: 'AK', label: i18n.t('global.states.ak') },
            { value: 'AZ', label: i18n.t('global.states.az') },
            { value: 'AR', label: i18n.t('global.states.ar') },
            { value: 'CA', label: i18n.t('global.states.ca') },
            { value: 'CO', label: i18n.t('global.states.co') },
            { value: 'CT', label: i18n.t('global.states.ct') },
            { value: 'DE', label: i18n.t('global.states.de') },
            { value: 'FL', label: i18n.t('global.states.fl') },
            { value: 'GA', label: i18n.t('global.states.ga') },
            { value: 'HI', label: i18n.t('global.states.hi') },
            { value: 'ID', label: i18n.t('global.states.id') },
            { value: 'IL', label: i18n.t('global.states.il') },
            { value: 'IN', label: i18n.t('global.states.in') },
            { value: 'IA', label: i18n.t('global.states.ia') },
            { value: 'KS', label: i18n.t('global.states.ks') },
            { value: 'KY', label: i18n.t('global.states.ky') },
            { value: 'LA', label: i18n.t('global.states.la') },
            { value: 'ME', label: i18n.t('global.states.me') },
            { value: 'MD', label: i18n.t('global.states.md') },
            { value: 'MA', label: i18n.t('global.states.ma') },
            { value: 'MI', label: i18n.t('global.states.mi') },
            { value: 'MN', label: i18n.t('global.states.mn') },
            { value: 'MS', label: i18n.t('global.states.ms') },
            { value: 'MO', label: i18n.t('global.states.mo') },
            { value: 'MT', label: i18n.t('global.states.mt') },
            { value: 'NE', label: i18n.t('global.states.ne') },
            { value: 'NV', label: i18n.t('global.states.nv') },
            { value: 'NH', label: i18n.t('global.states.nh') },
            { value: 'NJ', label: i18n.t('global.states.nj') },
            { value: 'NM', label: i18n.t('global.states.nm') },
            { value: 'NY', label: i18n.t('global.states.ny') },
            { value: 'NC', label: i18n.t('global.states.nc') },
            { value: 'ND', label: i18n.t('global.states.nd') },
            { value: 'OH', label: i18n.t('global.states.oh') },
            { value: 'OK', label: i18n.t('global.states.ok') },
            { value: 'OR', label: i18n.t('global.states.or') },
            { value: 'PA', label: i18n.t('global.states.pa') },
            { value: 'RI', label: i18n.t('global.states.ri') },
            { value: 'SC', label: i18n.t('global.states.sc') },
            { value: 'SD', label: i18n.t('global.states.sd') },
            { value: 'TN', label: i18n.t('global.states.tn') },
            { value: 'TX', label: i18n.t('global.states.tx') },
            { value: 'UT', label: i18n.t('global.states.ut') },
            { value: 'VT', label: i18n.t('global.states.vt') },
            { value: 'VA', label: i18n.t('global.states.va') },
            { value: 'WA', label: i18n.t('global.states.wa') },
            { value: 'WV', label: i18n.t('global.states.wv') },
            { value: 'WI', label: i18n.t('global.states.wi') },
            { value: 'WY', label: i18n.t('global.states.wy') },
        ];
    },
};
